import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import WebsiteApiService from '../../ApiServices/WebsiteApiService/WebsiteApiService';
import AppApiService from '../../ApiServices/WebsiteApiService/AppApiService';
import { useAuth } from "../Auth/AuthContext";
import PublisherApiService from '../../ApiServices/PublisherApiService/PublisherApiService';
import { motion, AnimatePresence } from 'framer-motion';

const customStyles = {
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 50,
    },
    content: {
        inset: 'unset',
        padding: '0',
        borderRadius: '1rem',
        maxWidth: '600px',
        margin: 'auto',
        background: 'transparent',
        border: 'none',
    },
};

const AddWebsiteModalForm = ({ isOpen, closeModal }) => {
    const [formData, setFormData] = useState({
        Name: '',
        Url: '',
        Title: '',
        Description: '',
        AppWeb: 'Website',
        UserId: '',
    });
    const [publishers, setPublishers] = useState([]);
    const [loading, setLoading] = useState(false);

    const { authState } = useAuth();
    const user = authState.user;
    const role = authState.roles;
    const admin = role === "Admin";

    useEffect(() => {
        if (admin) fetchPublishers();
    }, [admin]);

    const fetchPublishers = async () => {
        try {
            const response = await PublisherApiService.getAllApproved();
            setPublishers(response.data);
        } catch (error) {
            toast.error("Failed to load publishers");
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const appWebElement = document.querySelector('input[name="AppWeb"]:checked');
        if (!appWebElement) {
            toast.error("Please select either Website or App");
            setLoading(false);
            return;
        }
        const appWeb = appWebElement.value;
        const selectedUserId = admin ? formData.UserId : user.id;

        try {
            let result;
            if (appWeb === "Website") {
                if (!formData.Name || !formData.Url) {
                    toast.error("Name and URL are required for a Website");
                    setLoading(false);
                    return;
                }
                const websiteData = {
                    Name: formData.Name,
                    Url: formData.Url,
                    Title: formData.Title || "",
                    Description: formData.Description || "",
                    AppWeb: appWeb,
                    UserId: selectedUserId,
                };
                result = await WebsiteApiService.Add(websiteData);
            } else if (appWeb === "App") {
                if (!formData.AppId || !formData.AppName || !formData.AppUrl) {
                    toast.error("AppId, AppName, and AppUrl are required for an App");
                    setLoading(false);
                    return;
                }
                const appData = {
                    AppId: formData.AppId,
                    Name: formData.AppName,
                    Url: formData.AppUrl,
                    Title: formData.Title || "",
                    Description: formData.Description || "",
                    UserId: selectedUserId,
                };
                result = await AppApiService.Add(appData);
            }
            toast.success(result?.message || "Successfully added");
            closeModal();
            setFormData({
                Name: "",
                Url: "",
                AppId: "",
                AppName: "",
                AppUrl: "",
                Title: "",
                Description: "",
                AppWeb: "Website",
                UserId: "",
            });
        } catch (error) {
            toast.error(error.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <ToastContainer />
            <Modal isOpen={isOpen} onRequestClose={closeModal} style={customStyles} ariaHideApp={false}>
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <motion.div
                                initial={{ scale: 0.9, y: 20 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.9, y: 20 }}
                                transition={{ duration: 0.3 }}
                                className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-lg"
                            >
                                <h2 className="text-2xl font-bold text-center text-indigo-600 mb-6">Add Website / App</h2>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="flex justify-center gap-6 mb-4">
                                        {['Website', 'App'].map((option) => (
                                            <label key={option} className="flex items-center gap-2 text-gray-700 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="AppWeb"
                                                    value={option}
                                                    checked={formData.AppWeb === option}
                                                    onChange={handleInputChange}
                                                    className="form-radio text-indigo-500 focus:ring-indigo-500"
                                                />
                                                {option}
                                            </label>
                                        ))}
                                    </div>

                                    {admin && (
                                        <div className="flex flex-col">
                                            <label htmlFor="UserId" className="text-gray-600 mb-1">Select Publisher</label>
                                            <select
                                                id="UserId"
                                                name="UserId"
                                                value={formData.UserId}
                                                onChange={handleInputChange}
                                                className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            >
                                                <option value="">Select Publisher</option>
                                                {publishers.map((publisher) => (
                                                    <option key={publisher.id} value={publisher.id}>{publisher.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {formData.AppWeb === 'Website' && (
                                        <>
                                            {['Name', 'Url'].map((field) => (
                                                <div key={field} className="flex flex-col">
                                                    <label htmlFor={field} className="text-gray-600 mb-1">{field}</label>
                                                    <input
                                                        type="text"
                                                        id={field}
                                                        name={field}
                                                        value={formData[field] || ''}
                                                        onChange={handleInputChange}
                                                        className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                        placeholder={`Enter ${field}`}
                                                    />
                                                </div>
                                            ))}
                                        </>
                                    )}

                                    {formData.AppWeb === 'App' && (
                                        <>
                                            {['AppId', 'AppName', 'AppUrl'].map((field) => (
                                                <div key={field} className="flex flex-col">
                                                    <label htmlFor={field} className="text-gray-600 mb-1">{field}</label>
                                                    <input
                                                        type="text"
                                                        id={field}
                                                        name={field}
                                                        value={formData[field] || ''}
                                                        onChange={handleInputChange}
                                                        className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                        placeholder={`Enter ${field}`}
                                                    />
                                                </div>
                                            ))}
                                        </>
                                    )}

                                    <div className="grid grid-cols-2 gap-4 pt-4">
                                        <button
                                            type="button"
                                            onClick={closeModal}
                                            className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition duration-200"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200"
                                            disabled={loading}
                                        >
                                            {loading ? "Adding..." : "Add"}
                                        </button>
                                    </div>
                                </form>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Modal>
        </>
    );
};

export default AddWebsiteModalForm;
